import React from "react";
import { get, isNil, groupBy, isEqual } from "lodash";

import CSSModules from "lib/utils/css-modules";
import { FacilityDecorator, FormMessage, ProductVariantDecorator } from "lib/index";
import AppSettings from "lib/services/AppSettings";
import * as FormControl from "lib/components/FormControl";
import CategoryFacilitiesFormSection from "./CategoryFacilitiesFormSection";
import { ACCOMMODATION_UNIT_RANGES } from "../constants";

import styles from "./ProductVariantFormSection.less";

@CSSModules(styles)
export default class ProductVariantFormSection extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      possibleFacilitiesByCategory: this.getPossibleFacilitiesByCategory(props.facilities),
    };
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.facilities, this.props.facilities)) {
      this.setState({
        possibleFacilitiesByCategory: this.getPossibleFacilitiesByCategory(this.props.facilities),
      });
    }
  }

  getPossibleFacilitiesByCategory(facilities) {
    const possibleFacilities = facilities.filter(({ types }) => types.includes("product_variant"));
    return groupBy(possibleFacilities, "category");
  }

  handleProductVariantPropChange(propKey, propValue) {
    const { product_variant, onChange } = this.props;
    onChange(
      new ProductVariantDecorator({
        ...product_variant,
        [propKey]: propValue,
      })
    );
  }

  handleAccommodationDetailPropChange(propKey, propValue) {
    const { product_variant } = this.props;
    this.handleProductVariantPropChange("accommodation_detail", {
      ...(product_variant.accommodation_detail || {}),
      [propKey]: propValue,
    });
  }

  handleFacilityChange(id, checked) {
    const {
      product_variant: { facility_product_variants },
    } = this.props;
    let nextFacilityProductVariants = checked
      ? [...facility_product_variants, { facility_id: id }]
      : facility_product_variants.filter((facility) => facility.facility_id !== id);
    nextFacilityProductVariants = nextFacilityProductVariants.map((facility) => new FacilityDecorator(facility));
    this.handleProductVariantPropChange("facility_product_variants", nextFacilityProductVariants);
  }

  getProductVariantProp(propKey) {
    const { product_variant } = this.props;
    const propValue = get(product_variant, propKey);
    return isNil(propValue) ? null : propValue;
  }

  render() {
    const { errors, name, product_variant, product_price_currency, onBlur, readOnlyAccess } = this.props;
    const { possibleFacilitiesByCategory } = this.state;

    return (
      <div>
        {!!errors[name] && <FormMessage error={errors[name]} />}
        <div styleName="section">
          <FormControl.SelectFormGroup
            {...FormControl.addProps(`${name}.accommodation_detail.category`, { errors })}
            value={this.getProductVariantProp("accommodation_detail.category")}
            onBlur={onBlur}
            onChange={this.handleAccommodationDetailPropChange.bind(this, "category")}
            options={AppSettings.accommodation_detail_categories.map((category) => [
              category,
              I18n.t(
                `papp.products.EditProductView.StepAccommodationSettings.accommodation_settings.categoryOption.${category}`
              ),
            ])}
            className="u-massiveWidth"
            label={I18n.t("papp.products.EditProductView.StepAccommodationSettings.accommodation_settings.category")}
            readOnly={readOnlyAccess}
            required
          />
          <FormControl.NumberSwitchFormGroup
            {...FormControl.addProps(`${name}.default_total_stock_limit`, { errors })}
            value={this.getProductVariantProp("default_total_stock_limit")}
            onBlur={onBlur}
            onChange={this.handleProductVariantPropChange.bind(this, "default_total_stock_limit")}
            showCounter={true}
            label={I18n.t("papp.products.EditProductView.StepAccommodationSettings.default_total_stock_limit")}
            min={0}
            max={ACCOMMODATION_UNIT_RANGES.MAX_DEFAULT_TOTAL_STOCK_LIMIT}
            inlineLabel
            readOnly={readOnlyAccess}
            required
          />
          <FormControl.MoneyFormGroup
            {...FormControl.addProps(`${name}.presented_price`, { errors })}
            value={this.getProductVariantProp("presented_price")}
            onBlur={onBlur}
            onChange={this.handleProductVariantPropChange.bind(this, "presented_price")}
            label={I18n.t("papp.products.EditProductView.StepAccommodationSettings.presented_price")}
            inlineLabel
            currencyEditable={false}
            defaultCurrency={product_price_currency}
            min={0}
            readOnly={readOnlyAccess}
            required
          />
        </div>
        <div styleName="section">
          <h3>{I18n.t("papp.products.EditProductView.StepAccommodationSettings.accommodationUnitSettings")}</h3>
          <FormControl.NumberSwitchFormGroup
            {...FormControl.addProps(`${name}.accommodation_detail.single_beds_count`, { errors })}
            value={this.getProductVariantProp("accommodation_detail.single_beds_count")}
            onBlur={onBlur}
            onChange={this.handleAccommodationDetailPropChange.bind(this, "single_beds_count")}
            showCounter={true}
            label={I18n.t(
              "papp.products.EditProductView.StepAccommodationSettings.accommodation_detail.single_beds_count"
            )}
            min={0}
            max={ACCOMMODATION_UNIT_RANGES.MAX_BEDS_COUNT}
            inlineLabel
            readOnly={readOnlyAccess}
            required
          />
          <FormControl.NumberSwitchFormGroup
            {...FormControl.addProps(`${name}.accommodation_detail.double_beds_count`, { errors })}
            value={this.getProductVariantProp("accommodation_detail.double_beds_count")}
            onBlur={onBlur}
            onChange={this.handleAccommodationDetailPropChange.bind(this, "double_beds_count")}
            showCounter={true}
            label={I18n.t(
              "papp.products.EditProductView.StepAccommodationSettings.accommodation_detail.double_beds_count"
            )}
            min={0}
            max={ACCOMMODATION_UNIT_RANGES.MAX_BEDS_COUNT}
            inlineLabel
            readOnly={readOnlyAccess}
            required
          />
          <FormControl.NumberSwitchFormGroup
            {...FormControl.addProps(`${name}.accommodation_detail.extra_beds_count`, { errors })}
            value={this.getProductVariantProp("accommodation_detail.extra_beds_count")}
            onBlur={onBlur}
            onChange={this.handleAccommodationDetailPropChange.bind(this, "extra_beds_count")}
            showCounter={true}
            label={I18n.t(
              "papp.products.EditProductView.StepAccommodationSettings.accommodation_detail.extra_beds_count"
            )}
            min={0}
            max={ACCOMMODATION_UNIT_RANGES.MAX_BEDS_COUNT}
            inlineLabel
            readOnly={readOnlyAccess}
            required
          />
          <FormControl.NumberSwitchFormGroup
            {...FormControl.addProps(`${name}.accommodation_detail.rooms_count`, { errors })}
            value={this.getProductVariantProp("accommodation_detail.rooms_count")}
            onBlur={onBlur}
            onChange={this.handleAccommodationDetailPropChange.bind(this, "rooms_count")}
            showCounter={true}
            label={I18n.t("papp.products.EditProductView.StepAccommodationSettings.accommodation_detail.rooms_count")}
            min={0}
            max={ACCOMMODATION_UNIT_RANGES.MAX_ROOMS_COUNT}
            inlineLabel
            readOnly={readOnlyAccess}
            required
          />
          <FormControl.NumberSwitchFormGroup
            {...FormControl.addProps(`${name}.accommodation_detail.bathrooms_count`, { errors })}
            value={this.getProductVariantProp("accommodation_detail.bathrooms_count")}
            onBlur={onBlur}
            onChange={this.handleAccommodationDetailPropChange.bind(this, "bathrooms_count")}
            showCounter={true}
            label={I18n.t(
              "papp.products.EditProductView.StepAccommodationSettings.accommodation_detail.bathrooms_count"
            )}
            min={0}
            max={ACCOMMODATION_UNIT_RANGES.MAX_ROOMS_COUNT}
            inlineLabel
            readOnly={readOnlyAccess}
            required
          />
          <FormControl.InputFormGroup
            {...FormControl.addProps(`${name}.accommodation_detail.area`, { errors })}
            value={this.getProductVariantProp("accommodation_detail.area")}
            onBlur={onBlur}
            onChange={this.handleAccommodationDetailPropChange.bind(this, "area")}
            type="number"
            className="u-smallWidth"
            label={I18n.t("papp.products.EditProductView.StepAccommodationSettings.accommodation_detail.area")}
            min={0}
            inlineLabel
            inputLabel="m2"
            readOnly={readOnlyAccess}
          />
        </div>
        <div styleName="section">
          <h3>{I18n.t("papp.products.EditProductView.StepAccommodationSettings.facilities")}</h3>
          {Object.keys(possibleFacilitiesByCategory).map((category) => (
            <CategoryFacilitiesFormSection
              key={`categoryFacilities_${category}`}
              facility_product_variants={product_variant.facility_product_variants}
              errors={errors}
              productVariantFormSectionName={name}
              onBlur={onBlur}
              onFacilityChange={this.handleFacilityChange.bind(this)}
              title={I18n.t(
                `papp.products.EditProductView.StepAccommodationSettings.accommodation_settings.categoryTitle.${category}`
              )}
              categoryFacilities={possibleFacilitiesByCategory[category]}
              readOnlyAccess={readOnlyAccess}
            />
          ))}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/products/EditProductView/StepAccommodationSettings/ProductVariantFormSection.jsx